import { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Stack,
    Typography,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import StripedDataGrid from '../../../Shared/Components/Table/StripedDataGrid';
import { processedTimesheetDetailsTableColumns } from '../../../Shared/Utils/processedTimesheetDetailsTableColumns';
import ProcessedTimesheetLineDetailModal from './ProcessedTimesheetLineDetailModal';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import useDeleteProcessedTimeSheetEntry from '../../hooks/useDeleteProcessedTimesheetEntry';
import { useToast } from '../../Contexts/ToastContext';
import { formatUtcDate } from '../../../utils/formatDate';
import { useProcessedTimesheetContext } from '../../Contexts/ProcessedTimesheetContext';
import { currencyFormatter, trimLeadingAndTrailingQuotes } from '../../../utils/helpers';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';
import Switch from '../../../Shared/Components/Switch';
import ToolTipComponent from '../../../components/GlobalComponents/ToolTipComponent';
import { PROCESSED_TIMESHEET_ENTRY_INVOICE_STATUS_ENUM } from '../../Configurations/processedTimesheetsConfig';

const trimPTSEntryToAggregate = (entry) => ({
    ...entry,
    id: undefined,
    processedTimesheetId: undefined,
    locationId: undefined,
    locationName: undefined,
    createdBy: undefined,
    created: undefined,
    modifiedBy: undefined,
    modified: undefined,
    lastModified: undefined,
});

const getAggregatedEntryKey = ({
    date,
    processedAsHoliday,
    isBillable,
    rateTypeId,
    billRate,
    billUnit,
    payRate,
    payUnit,
}) => {
    const trimmedDate = date.split('T')?.[0] || '';
    return [
        rateTypeId,
        billRate || payRate,
        billUnit || payUnit,
        processedAsHoliday,
        isBillable || false,
        trimmedDate,
    ].join('-');
};

const entryPropertiesToAggregate = [
    'aB5Amount',
    'billAmount',
    'billMiles',
    'billTime',
    'discountAmount',
    'payAmount',
    'payTime',
    'payMiles',
    'rebateAmount',
];

const ProcessedTimesheetDetailsTable = ({
    processedTimesheetId,
    data,
    isLoading,
    initialSortModel,
    availableRates,
    weekEndingStartDate = null,
    weekEndingEndDate = null,
    hideOpenButton = false,
    refetch,
    locations = [],
    locationsPTSIds = [],
    readOnly = false,
    splitByWorkLocation: initialSplitByWorkLocation,
}) => {
    const { showSuccessToast, showErrorToast } = useToast();
    const { handleOutdatedError, calculationErrorEntryIds, syncCalculationErrorEntryIds } =
        useProcessedTimesheetContext();
    const { isTimesheetSystemAdminUser } = useAdminAuthContext();

    const [showAggregate, setShowAggregate] = useState(false);
    useEffect(() => {
        if (typeof initialSplitByWorkLocation !== 'boolean') return;
        setShowAggregate(!initialSplitByWorkLocation);
    }, [initialSplitByWorkLocation]);

    const [modalState, setModalState] = useState({
        open: false,
        editEntry: false,
        newEntry: false,
        selectedRow: null,
    });
    const [sortQueryParams, setSortQueryParams] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        syncCalculationErrorEntryIds(data, locations);
    }, [data, locations, syncCalculationErrorEntryIds]);

    const calculationErrorEntryIdSet = useMemo(
        () => new Set(calculationErrorEntryIds),
        [calculationErrorEntryIds]
    );

    const getRowClassName = useCallback(
        (params) => (calculationErrorEntryIdSet.has(params.row.id) ? 'error-row' : ''),
        [calculationErrorEntryIdSet]
    );

    const {
        mutate: deleteProcessedTimesheetEntry,
        isLoading: deleteProcessedTimesheetEntryLoading,
    } = useDeleteProcessedTimeSheetEntry({
        onSuccess: () => {
            refetch();
            showSuccessToast('Entry deleted successfully', {
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
            setShowDeleteConfirmation(false);
        },
        onError: (error) => {
            handleOutdatedError(error, () => {
                const errorMessage =
                    error?.message && typeof error?.message === 'string'
                        ? trimLeadingAndTrailingQuotes(error?.message)
                        : 'There was an error deleting the entry. Please try again.';
                showErrorToast(errorMessage, {
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                });
                console.error('useDeleteProcessedTimesheetEntry:', error);
            });
        },
    });

    const handleSortModelChange = (sortModel) => {
        const params = new URLSearchParams({
            sortField: sortModel[0]?.field || 'date',
            sortDirection: sortModel[0]?.sort || 'asc',
        }).toString();
        setSortQueryParams(params);
    };

    const sortedData = useMemo(
        () =>
            data?.slice().sort((a, b) => {
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            }),
        [data]
    );

    const groupedDataByLocation = useMemo(() => {
        if (!sortedData || !locations || locations.length === 0) {
            return [];
        }

        // Group entries by locationId
        const entriesByLocation = locations.map((location) => {
            const locationEntries = sortedData.filter(
                (entry) => entry.locationId === location.locationId
            );

            // Calculate totals for this location
            const { totalBillAmount, totalPayAmount, locationPTSIds } = locationEntries.reduce(
                (sum, entry) => ({
                    totalBillAmount: sum.totalBillAmount + (parseFloat(entry.billAmount) || 0),
                    totalPayAmount: sum.totalPayAmount + (parseFloat(entry.payAmount) || 0),
                    locationPTSIds: sum.locationPTSIds.add(entry.processedTimesheetId),
                }),
                { totalBillAmount: 0, totalPayAmount: 0, locationPTSIds: new Set() }
            );

            return {
                locationId: location.locationId,
                locationName: location.locationName,
                entries: locationEntries,
                totalBillAmount,
                totalPayAmount,
                locationPTSIds: Array.from(locationPTSIds).filter((id) => !!id),
            };
        });

        // Filter out locations with no entries
        return entriesByLocation.filter((location) => location.entries.length > 0);
    }, [sortedData, locations]);

    const aggregatedData = useMemo(() => {
        if (!sortedData || sortedData.length === 0) return [];
        const trimmedData = sortedData.map(trimPTSEntryToAggregate);

        // Entries with same date and rateTypeId, {bill/pay}Rate, {bill/pay}Unit, processAsHoliday & isBillable are grouped together and summed up
        const groupedAggregatedData = trimmedData.reduce((acc, entry) => {
            const aggregatedEntryKey = getAggregatedEntryKey(entry);

            if (!acc[aggregatedEntryKey]) {
                // Initialize the entry for that key
                acc[aggregatedEntryKey] = { ...entry };
            } else {
                // Sum the numeric values for that key (except billRate, payRate, rate)
                const aggregatedEntry = { ...acc[aggregatedEntryKey] };
                entryPropertiesToAggregate.forEach((property) => {
                    const value = entry?.[property];
                    if (typeof value === 'number' && value !== 0) {
                        aggregatedEntry[property] += value;
                    }
                });
                if (aggregatedEntry.invoiceStatus !== entry.invoiceStatus) {
                    aggregatedEntry.invoiceStatus =
                        PROCESSED_TIMESHEET_ENTRY_INVOICE_STATUS_ENUM.NotInvoiced;
                    aggregatedEntry.invoiceNumber = undefined;
                } else if (aggregatedEntry.invoiceNumber !== entry.invoiceNumber) {
                    aggregatedEntry.invoiceNumber = undefined;
                }
                aggregatedEntry.isPosted = aggregatedEntry.isPosted && entry.isPosted;
                acc[aggregatedEntryKey] = aggregatedEntry;
            }

            return acc;
        }, {});
        return Object.values(groupedAggregatedData);
    }, [sortedData]);

    const handleView = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            editEntry: false,
            selectedRow: row,
        }));
    };

    const handleEdit = (row) => {
        if (row?.isPosted) return;
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            editEntry: true,
            selectedRow: row,
        }));
    };

    const handleDelete = (row) => {
        if (row?.isPosted) return;
        deleteProcessedTimesheetEntry({
            id: row.id,
            isMileage: row.isMileage ?? false,
            lastModified: row.lastModified,
        });
    };

    const handleClose = () => {
        setModalState((prevState) => ({
            ...prevState,
            open: false,
            editEntry: false,
            newEntry: false,
            selectedRow: null,
        }));
    };

    const openEmptyForm = () => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            newEntry: true,
            selectedRow: {},
        }));
    };

    const handleEditModeChange = () => {
        setModalState((prevState) => {
            if (prevState.selectedRow?.isPosted) return prevState;
            return {
                ...prevState,
                editEntry: true,
            };
        });
    };

    const handleDuplicate = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            newEntry: true,
            selectedRow: { ...row, id: undefined },
        }));
    };

    const setSelectedRow = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            selectedRow: row,
        }));
    };

    const allLocationIds = useMemo(
        () => groupedDataByLocation?.map(({ locationId }) => locationId) ?? [],
        [groupedDataByLocation]
    );
    const [expanded, setExpanded] = useState([]);
    useEffect(() => {
        setExpanded(allLocationIds); // expand all locations by default
    }, [allLocationIds]);
    const handleChangeExpand = (panel) => (event, expand) => {
        setExpanded((prevState) =>
            expand ? [...prevState, panel] : prevState.filter((id) => id !== panel)
        );
    };
    const isAllExpanded = expanded.length === allLocationIds.length;
    const handleExpandAll = () => {
        setExpanded(allLocationIds);
    };
    const handleCollapseAll = () => {
        setExpanded([]);
    };

    const renderLocationPTSIdsTooltip = (ids = []) =>
        ids?.length > 0 && (
            <ToolTipComponent
                arrow
                title="Location-specific ID: Use this to find the timesheet in Workday or internal systems."
                placement="bottom-start"
            >
                <Typography
                    fontSize="12px"
                    component="span"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                >
                    Location IDs: {ids?.join(', ')}{' '}
                    <InfoIcon sx={{ fontSize: 'inherit', display: 'inline-block' }} />
                </Typography>
            </ToolTipComponent>
        );

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontWeight: 'bold', color: COLORS.LT_JOURNEY175 }}>
                    Processed Timesheet Details
                    <br />
                    {showAggregate && renderLocationPTSIdsTooltip(locationsPTSIds)}
                </Typography>

                {!showAggregate && allLocationIds.length > 1 && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={isAllExpanded ? handleCollapseAll : handleExpandAll}
                        sx={{ ml: 2, mr: 'auto' }}
                    >
                        {isAllExpanded ? 'Collapse all' : 'Expand all'}{' '}
                        <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                                transform: isAllExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    </Button>
                )}
                <Stack direction="row">
                    {initialSplitByWorkLocation && (
                        <FormControlLabel
                            label="Show Aggregate"
                            labelPlacement="start"
                            sx={{ mx: 2, color: COLORS.LT_JOURNEY }}
                            slotProps={{ typography: { sx: { fontSize: '12px' } } }}
                            control={<Switch />}
                            checked={showAggregate}
                            onChange={() => setShowAggregate(!showAggregate)}
                        />
                    )}
                    {!readOnly && isTimesheetSystemAdminUser && (
                        <Button
                            disableRipple
                            onClick={() => openEmptyForm()}
                            endIcon={<AddIcon sx={{ fontSize: '12px' }} />}
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: COLORS.LT_JOURNEY,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0,
                                padding: 0,
                                '& .MuiButton-endIcon': { margin: 0, marginLeft: '8px' },
                                marginRight: 2,
                            }}
                        >
                            New Item
                        </Button>
                    )}
                    {!hideOpenButton && (
                        <Button
                            href={`#/processed-timesheets/${processedTimesheetId}/details?${sortQueryParams}`}
                            target="_blank"
                            endIcon={<OpenInNewIcon sx={{ fontSize: '12px' }} />}
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: COLORS.LT_JOURNEY,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0,
                                padding: 1,
                                mr: -0.5,
                            }}
                            disableRipple
                        >
                            Open In New Tab
                        </Button>
                    )}
                </Stack>
            </Stack>
            {!showAggregate && groupedDataByLocation.length > 0 ? (
                <Stack gap="20px">
                    {groupedDataByLocation.map((locationGroup) => (
                        <Accordion
                            variant="lt-admin-primary"
                            key={locationGroup.locationId}
                            defaultExpanded
                            expanded={expanded.includes(locationGroup.locationId)}
                            onChange={handleChangeExpand(locationGroup.locationId)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                                <div>
                                    <Typography fontSize="16px" lineHeight="120%">
                                        {locationGroup.locationName}
                                    </Typography>
                                    {renderLocationPTSIdsTooltip(locationGroup?.locationPTSIds)}
                                </div>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap="30px"
                                    ml="auto"
                                    mr="30px"
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <span style={{ fontSize: 9, textTransform: 'uppercase' }}>
                                            Total Bill:
                                        </span>
                                        {currencyFormatter(locationGroup.totalBillAmount)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <span style={{ fontSize: 9, textTransform: 'uppercase' }}>
                                            Total Pay:
                                        </span>
                                        {currencyFormatter(locationGroup.totalPayAmount)}
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <StripedDataGrid
                                    columns={processedTimesheetDetailsTableColumns}
                                    rows={locationGroup.entries}
                                    showOptions
                                    onView={handleView}
                                    onEdit={handleEdit}
                                    onDelete={(selectedRow) => {
                                        setShowDeleteConfirmation(true);
                                        setSelectedRow(selectedRow);
                                    }}
                                    isLoading={isLoading}
                                    initialSortModel={
                                        initialSortModel ?? [{ field: 'date', sort: 'asc' }]
                                    }
                                    onSortModelChange={handleSortModelChange}
                                    readOnly={readOnly || !isTimesheetSystemAdminUser}
                                    getRowClassName={getRowClassName}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Stack>
            ) : (
                <StripedDataGrid
                    columns={processedTimesheetDetailsTableColumns}
                    rows={initialSplitByWorkLocation ? aggregatedData : sortedData}
                    showOptions
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={(selectedRow) => {
                        setShowDeleteConfirmation(true);
                        setSelectedRow(selectedRow);
                    }}
                    isLoading={isLoading}
                    initialSortModel={initialSortModel ?? [{ field: 'date', sort: 'asc' }]}
                    onSortModelChange={handleSortModelChange}
                    readOnly={initialSplitByWorkLocation || readOnly || !isTimesheetSystemAdminUser}
                    getRowClassName={getRowClassName}
                />
            )}
            <ProcessedTimesheetLineDetailModal
                isOpen={modalState.open}
                onClose={handleClose}
                editMode={modalState.editEntry}
                addMode={modalState.newEntry}
                data={modalState.selectedRow}
                onEditModeChange={handleEditModeChange}
                availableRates={availableRates}
                weekEndingStartDate={weekEndingStartDate}
                weekEndingEndDate={weekEndingEndDate}
                processedTimesheetId={processedTimesheetId}
                refetch={refetch}
                locations={locations}
                onDuplicate={handleDuplicate}
                readOnly={readOnly || !isTimesheetSystemAdminUser}
                isAggregatePts={!initialSplitByWorkLocation}
            />
            <Dialog
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                slotProps={{ paper: { sx: { borderRadius: '8px', padding: '24px' } } }}
            >
                <DialogContent>
                    <IconButton
                        disabled={false}
                        onClick={() => setShowDeleteConfirmation(false)}
                        sx={{ position: 'absolute', right: '10px', top: '5px' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Stack>
                        <Typography sx={{ color: COLORS.LT_FOUNDATION125 }}>
                            Are you sure you want to delete the {modalState?.selectedRow?.rateType}{' '}
                            detail from{' '}
                            {formatUtcDate(modalState?.selectedRow?.date, DATE_FORMATS.MD)}?
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                            color="secondary"
                            variant="contained"
                            sx={{ mr: 1 }}
                            onClick={() => setShowDeleteConfirmation(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="error"
                            onClick={() => handleDelete(modalState?.selectedRow)}
                            loading={deleteProcessedTimesheetEntryLoading}
                        >
                            Yes, delete detail
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ProcessedTimesheetDetailsTable;
